<script setup>
import { ref, watchEffect, computed } from 'vue';
import axios from 'axios';
import Swal from 'sweetalert2';
import SearchFields from './SearchFields.vue';

const props = defineProps(['fetchUrl', 'deleteUrl', 'state']);

const filterParameters = [
  { index: "rechnungsempfaenger", title: "Kundennummer", value: null }
];

const currentPage = ref(1);
const maxPageNumber = ref(1);
const footerPageNumbers = ref([]);
const sortBy = ref('change_date'); // default sort field (Änderungsdatum)
const order = ref('asc'); // default order
const filterValue = ref("");

const columnsToFetch = ['id', 'rechnungsempfaenger', 'teilnehmer', 'tarifnummer', 'original_tarifnummer', 'change_date', 'status', 'created_at'];
const tarifChangeList = ref([]);

function buildUrl() {
  let url = `${props.fetchUrl}?page=${currentPage.value}&per_page=25&columns=${columnsToFetch.join(',')}`;
  url += `&sortBy=${sortBy.value}&order=${order.value}`;
  if (filterValue.value) {
    url += '&filter=' + encodeURIComponent(JSON.stringify([{ index: 'rechnungsempfaenger', value: filterValue.value }]));
  }
  return url;
}

async function fetchData() {
  const url = buildUrl();
  try {
    const res = await axios.get(url);
    tarifChangeList.value = res.data.data;
    maxPageNumber.value = res.data.meta.last_page;
    buildFooter();
  } catch (error) {
    console.error(error);
  }
}

function buildFooter() {
  let pages = [];
  for (let i = 1; i <= maxPageNumber.value; i++) {
    pages.push(i);
  }
  footerPageNumbers.value = pages;
}

function setSort(column) {
  if (sortBy.value === column) {
    order.value = (order.value === 'asc') ? 'desc' : 'asc';
  } else {
    sortBy.value = column;
    order.value = 'asc';
  }
  currentPage.value = 1;
  fetchData();
}

function getNewPage(newPage) {
  if (newPage >= 1 && newPage <= maxPageNumber.value) {
    currentPage.value = newPage;
    fetchData();
  }
}

async function deleteTarifChange(id) {
  Swal.fire({
    title: "Tarifänderung löschen?",
    text: "Wenn Sie eine wartende Tarifänderung löschen, wird diese nicht mehr durchgeführt.",
    icon: 'warning',
    confirmButtonText: "Löschen",
    confirmButtonColor: "#e64942",
    showCancelButton: true,
    cancelButtonText: "Abbrechen",
    focusCancel: true,
    reverseButtons: true,
  }).then((result) => {
    if (result.isConfirmed) {
      axios.delete(props.deleteUrl.replace(':id', id))
        .then((res) => {
          if (res.status === 200) {
            Swal.fire({
              title: 'Löschen erfolgreich',
              text: 'Tarifänderung wurde erfolgreich gelöscht!',
              icon: 'success',
            }).then(() => { fetchData(); });
          }
        }).catch((error) => {
          Swal.fire({
            title: 'Löschen fehlgeschlagen!',
            text: 'Leider ist das Löschen fehlgeschlagen. ' + (error.response.data.message || ''),
            icon: 'error',
          });
        });
    }
  });
}

function filterSearch(givenFields) {
  const r = givenFields.find(f => f.index === "rechnungsempfaenger");
  filterValue.value = r && r.value ? r.value : "";
  getNewPage(1);
}

watchEffect(() => {
  fetchData();
});

// New computed property to limit the number of visible pages
const visiblePagesCount = 27;
const visiblePages = computed(() => {
  const start = Math.max(1, currentPage.value - Math.floor(visiblePagesCount / 2));
  let end = start + visiblePagesCount - 1;
  if (end > maxPageNumber.value) {
    end = maxPageNumber.value;
  }
  const adjustedStart = Math.max(1, end - visiblePagesCount + 1);
  const pages = [];
  for (let i = adjustedStart; i <= end; i++) {
    pages.push(i);
  }
  return pages;
});
</script>

<template>
  <div>
    <SearchFields :fields="filterParameters" @filters="filterSearch" />

    <div class="main-table" v-if="tarifChangeList && tarifChangeList.length > 0">
      <div class="inner-table">
        <table class="table table-hover" style="table-layout: fixed; width: 100%;">
          <thead>
            <tr>
              <th @click="setSort('id')"
                  :class="{'active': sortBy === 'id', 'asc': (sortBy === 'id' && order==='asc'), 'desc': (sortBy === 'id' && order==='desc')}"
                  style="width: 10%;">ID</th>
              <th @click="setSort('rechnungsempfaenger')"
                  :class="{'active': sortBy === 'rechnungsempfaenger', 'asc': (sortBy === 'rechnungsempfaenger' && order==='asc'), 'desc': (sortBy === 'rechnungsempfaenger' && order==='desc')}"
                  style="width: 17%;">Rechnungsempfänger</th>
              <th @click="setSort('teilnehmer')"
                  :class="{'active': sortBy === 'teilnehmer', 'asc': (sortBy === 'teilnehmer' && order==='asc'), 'desc': (sortBy === 'teilnehmer' && order==='desc')}"
                  style="width: 11%;">Teilnehmer</th>
              <th @click="setSort('tarifnummer')"
                  :class="{'active': sortBy === 'tarifnummer', 'asc': (sortBy === 'tarifnummer' && order==='asc'), 'desc': (sortBy === 'tarifnummer' && order==='desc')}"
                  style="width: 15%;">Neuer Tarif</th>
              <th @click="setSort('original_tarifnummer')"
                  :class="{'active': sortBy === 'original_tarifnummer', 'asc': (sortBy === 'original_tarifnummer' && order==='asc'), 'desc': (sortBy === 'original_tarifnummer' && order==='desc')}"
                  style="width: 15%;">Alter Tarif</th>
              <th @click="setSort('change_date')"
                  :class="{'active': sortBy === 'change_date', 'asc': (sortBy === 'change_date' && order==='asc'), 'desc': (sortBy === 'change_date' && order==='desc')}"
                  style="width: 15%;">Änderungsdatum</th>
              <th @click="setSort('status')"
                  :class="{'active': sortBy === 'status', 'asc': (sortBy === 'status' && order==='asc'), 'desc': (sortBy === 'status' && order==='desc')}"
                  style="width: 10%;">Status</th>
              <th @click="setSort('created_at')"
                  :class="{'active': sortBy === 'created_at', 'asc': (sortBy === 'created_at' && order==='asc'), 'desc': (sortBy === 'created_at' && order==='desc')}"
                  style="width: 12%;">Erstelldatum</th>
              <th style="width: 10%;">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="change in tarifChangeList" :key="change.id">
              <td>{{ change.id }}</td>
              <td>{{ change.rechnungsempfaenger }}</td>
              <td>{{ change.teilnehmer }}</td>
              <td>{{ change.tarifnummer }}</td>
              <td>{{ change.original_tarifnummer }}</td>
              <td>{{ change.change_date }}</td>
              <td>{{ change.status }}</td>
              <td>{{ change.created_at }}</td>
              <td>
                <div class="btn-group probInfLoeschen" role="group" >
                  <span v-if="(new Date(change.change_date) < new Date(Date.now() - 3 * 24 * 60 * 60 * 1000)) && change.status != 'done'" class="ml-2">
                    <i style="color: #db6379;" class="fa-solid fa-circle-exclamation text-danger" title="Bitte prüfen"></i>
                  </span>
                  <button v-if="change.status != 'done'" class="btn btn-primary btn-sm" @click.prevent="deleteTarifChange(change.id)">Löschen</button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="table-footer middle" v-if="footerPageNumbers.length > 1">
      <ul class="pagination">
        <li class="page-item">
          <button class="page-link" :disabled="currentPage === 1" @click="getNewPage(currentPage - 1)">
            <i class="fas fa-lg fa-caret-left"></i>
          </button>
        </li>
        <li v-for="page in visiblePages" :key="page" :class="{ 'active': page === currentPage }" class="page-item">
          <button class="page-link" @click="getNewPage(page)">{{ page }}</button>
        </li>
        <li class="page-item">
          <button class="page-link" :disabled="currentPage === maxPageNumber" @click="getNewPage(currentPage + 1)">
            <i class="fas fa-lg fa-caret-right"></i>
          </button>
        </li>
      </ul>
    </div>

    <div v-if="tarifChangeList.length === 0" class="m-3">
      <div class="alert alert-custom" role="alert">
        <i class="fa-solid fa-comment fa-4x"></i>
        <span>Es wurden keine Tarifänderungen gefunden.</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
table {
  width: 100%;
  table-layout: fixed;
}

th {
  cursor: pointer;
  position: relative;
  padding-right: 25px;
  transition: background-color 0.3s;
}

th:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

th.active {
  font-weight: bold;
}

th:after {
  content: "\f0dc";
  font-family: "Font Awesome 6 Free";
  font-weight: 900;
  position: absolute;
  right: 5px;
  opacity: 0.5;
}

th.asc:after {
  content: "\f0de";
  opacity: 1;
}

th.desc:after {
  content: "\f0dd";
  opacity: 1;
}

.table-controls {
  margin-bottom: 1em;
}

.probInfLoeschen {
    gap: 5px;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
}
</style>
